<app-card-widget title="Attachments" *ngIf="invoice$ | async">
  <form [formGroup]="form" id="attachment-v2-form" (ngSubmit)="save()" novalidate>
    <div class="card-section-container">
      <!-- AI: List view (default mode, hidden during add) -->
      <div class="card-section" *ngIf="!added">
        <mat-nav-list>
          <mat-list-item
            *ngFor="let item of attachments$ | async"
            (click)="itemClicked(item)"
            [class.mat-elevation-z2]="item === selectedItem"
          >
            <mat-icon matListIcon>attachment</mat-icon>
            <h3 matLine>{{ item.filename }}</h3>
            <span matLine>{{ item.type_description }}</span>
            <div class="list-item-timestamp">{{ item.created_at | date:'short' }}</div>
          </mat-list-item>
        </mat-nav-list>
      </div>

      <!-- AI: Detail view (selected item, not in edit or add mode) -->
      <div class="card-section" *ngIf="selectedItem && !edited && !added">
        <app-text label="Type">{{ selectedItem.type_description }}</app-text>
        <app-text label="Original Filename">{{ selectedItem.filename }}</app-text>
        <app-text label="Description">{{ selectedItem.description }}</app-text>
        <app-text label="Created"
          >{{ selectedItem.created_at | date:'medium' }}</app-text
        >

        <button
          type="button"
          mat-icon-button
          (click)="download(selectedItem)"
          title="Download Attachment File"
        >
          <mat-icon>cloud_download</mat-icon>
        </button>
      </div>

      <!-- AI: Edit form (editing selected item metadata) -->
      <div class="card-section" *ngIf="edited">
        <app-text label="Original Filename" *ngIf="selectedItem">{{ selectedItem.filename }}</app-text>

        <mat-form-field>
          <mat-select placeholder="Type" formControlName="attachment_type_id">
            <mat-option *ngFor="let type of attachmentTypes$ | async" [value]="type.id">
              {{ type.description }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <input matInput placeholder="Description" formControlName="description" />
        </mat-form-field>

        <mat-form-field>
          <mat-select placeholder="Hidden" formControlName="is_hidden">
            <mat-option *ngFor="let option of hiddenChoices" [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <app-text label="Created" *ngIf="selectedItem"
          >{{ selectedItem.created_at | date:'medium' }}</app-text
        >
      </div>

      <!-- AI: Upload section (add mode) - type selector + drop zone + file queue -->
      <div class="card-section" *ngIf="added" [formGroup]="uploadForm">
        <mat-form-field>
          <mat-select
            placeholder="Type (required)"
            formControlName="attachment_type_id"
          >
            <mat-option *ngFor="let type of attachmentTypes$ | async" [value]="type.id">
              {{ type.description }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <!-- AI: Combined drag-and-drop + click-to-browse zone.
             Uses native event handlers instead of the old appFileUpload directive
             for better control over drag-over visual feedback.
             Keyboard-operable in place: Enter and Space do what a click does, and Space must not also scroll the page. -->
        <div class="drop-zone"
             [class.drag-over]="isDragOver"
             (dragover)="onDragOver($event)"
             (dragleave)="onDragLeave($event)"
             (drop)="onDrop($event)"
             role="button"
             tabindex="0"
             (click)="fileInput.click()"
             (keydown.enter)="fileInput.click()"
             (keydown.space)="$event.preventDefault(); fileInput.click()">
          <mat-icon>cloud_upload</mat-icon>
          <p>Drag and drop files here, or click to browse</p>
          <input #fileInput
                 type="file"
                 multiple
                 hidden
                 (change)="onFileInputChange($event)" />
        </div>

        <!-- AI: Server-side rejection (e.g. a non-PDF Original Invoice). -->
        <p class="error-hint" *ngIf="uploadError">{{ uploadError }}</p>

        <!-- AI: File queue with names, sizes, and remove buttons -->
        <mat-list *ngIf="uploadFiles.length > 0">
          <mat-list-item *ngFor="let f of uploadFiles">
            <mat-icon matListIcon>attachment</mat-icon>
            <h3 matLine>{{ f.name }}</h3>
            <span matLine class="file-size">{{ formatFileSize(f.size) }}</span>
            <button type="button" mat-icon-button (click)="removeUploadFile(f)">
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-list-item>
        </mat-list>
      </div>
    </div>
  </form>

  <mat-card-actions align="end">
    <button
      type="button"
      mat-button
      *ngIf="!edited && !added"
      [disabled]="locked || watermarking"
      (click)="watermark()"
      matTooltip="Watermark original invoice and attachments"
    >
      Watermark
    </button>
    <button
      type="button"
      mat-button
      *ngIf="!edited && !added"
      [disabled]="locked"
      (click)="add()"
    >
      Add
    </button>
    <button
      type="button"
      mat-button
      *ngIf="!edited && !added"
      [disabled]="locked || !selectedItem"
      (click)="edit()"
    >
      Edit
    </button>
    <button
      type="submit"
      mat-button
      *ngIf="edited"
      [disabled]="!form.valid || saving"
      form="attachment-v2-form"
    >
      Save
    </button>
    <button
      type="button"
      mat-button
      *ngIf="added"
      [disabled]="uploadFiles.length === 0 || !uploadForm.valid || saving"
      (click)="upload()"
    >
      Save
    </button>
    <button type="reset" mat-button *ngIf="edited || added" (click)="revert()">
      Cancel
    </button>
  </mat-card-actions>
</app-card-widget>
